import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { $notificationStore, $transferRequestsCreateStore, $transferRequestsUpdateStore, $transferUpdateStatusStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import {
  ErrorCode,
  TransferRequestStatusEnum,
  VopStatus,
  type AccountResponse,
  type RecipientResponse,
  type TransferRequestResponse,
} from '@shared/api';
import type { AccountFormData, AddressFormData, CompanyFormData, FilesFormData, PaymentFormData, PersonalFormData } from '../formItem';
import { ConfirmModal, OTPModal, PaymentModal, TransferRequestConfirmModal } from '../modal';
import { RecipientVerificationModal, VerifiedRecipientModal } from '@widgets';
import { Spinner } from '@shared/widgets';
// import AlertIcon from '@assets/icons/alert.svg';
import ConfirmIcon from '@assets/icons/confirm.svg';

type TransferConfirmationDataType = PersonalFormData & CompanyFormData & AddressFormData & AccountFormData & PaymentFormData & FilesFormData;

interface TransferConfirmationBlockProps {
  showPaymentModal: boolean;
  onClosePaymentModal: () => void;
  account: AccountResponse;
  accountTo?: AccountResponse;
  recipient?: RecipientResponse | null;
  vopForceUpdate?: boolean;
}

export const TransferConfirmationBlock = (props: TransferConfirmationBlockProps) => {
  const { navigate } = useRouteHook();

  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferUpdateStatusStore = useUnit($transferUpdateStatusStore.store);

  const [showApprovalModal, setShowApprovalModal] = useState<TransferRequestResponse | null>(null);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);

  const { t } = useTranslation('payments');
  const { watch, setValue } = useFormContext<TransferConfirmationDataType>();
  const files = watch('files');

  const confirmHandler = async (vopForceUpdate?: boolean) => {
    if (!transferRequestsCreateStore.data?.id) return;
    const response = await $transferUpdateStatusStore.events.updateStatus({
      id: transferRequestsCreateStore.data.id,
      status: TransferRequestStatusEnum.WaitingForApproval,
      vopForceUpdate: props.vopForceUpdate || vopForceUpdate,
    });
    if (response.data) {
      props.onClosePaymentModal();
      setShowApprovalModal(response.data);
    }
  };

  const rejectHandler = () => {
    $transferRequestsCreateStore.events.clear();
    $transferRequestsUpdateStore.events.clear();
    $notificationStore.events.setWarning(t('notifications.rejected'));
  };

  const cancelHandler = async () => {
    if (!transferRequestsCreateStore.data?.id) return;
    await $transferUpdateStatusStore.events.updateStatus({ id: transferRequestsCreateStore.data.id, status: TransferRequestStatusEnum.Cancelled });
    navigate(RoutePaths.payments.onNavigate());
  };

  return (
    <>
      <Spinner show={transferUpdateStatusStore.loading} position />
      <PaymentModal
        isOpen={props.showPaymentModal && !transferUpdateStatusStore.error?.errorCode}
        onClose={props.onClosePaymentModal}
        account={props.account!}
        accountTo={props.accountTo}
        recipient={props.recipient}
        files={files}
        onUploadFile={(files) => setValue('files', files)}
        onDeleteFile={(files) => setValue('files', files)}
        onConfirm={confirmHandler}
      />
      {showApprovalModal && (
        <TransferRequestConfirmModal
          isOpen={!!showApprovalModal && transferUpdateStatusStore.data?.vopStatus !== VopStatus.Verified}
          onClose={(withRedirect) => (setShowApprovalModal(null), withRedirect && navigate(RoutePaths.payments.onNavigate()))}
          withRedirect
          onOpenOtpModal={(data) => (setShowOtpModal(data), setShowApprovalModal(null))}
          onCloseOtpModal={() => setShowOtpModal(null)}
          transactionId={showApprovalModal.id}
          transferData={showApprovalModal}
          noDocumentUpload
          onReject={rejectHandler}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        onCancel={() => (setShowApprovalModal(showOtpModal), setShowOtpModal(null))}
        data={showOtpModal!}
        onSubmit={(data) => setShowConfirmModal(data!)}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='mb-4 flex flex-col gap-2 text-center'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() =>
          showConfirmModal?.paymentId &&
          navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal.paymentId, { back: RoutePaths.payments.onNavigate() }))
        }
      />
      {/* <ConfirmModal
        isOpen={!!transferUpdateStatusStore.error}
        icon={<AlertIcon />}
        title={transferUpdateStatusStore.error ?? t('transferConfirmation.modals.confirm.error')}
        buttonTitle={t('buttons.return', { ns: 'common' })}
        onClick={() => $transferUpdateStatusStore.events.clear()}
      /> */}
      <VerifiedRecipientModal
        isOpen={
          transferUpdateStatusStore.data?.vopStatus === VopStatus.Verified &&
          transferUpdateStatusStore.data?.status === TransferRequestStatusEnum.WaitingForApproval
        }
        onClose={() => $transferUpdateStatusStore.events.clear()}
        buttonTitle='Continue payment'
      />
      <RecipientVerificationModal
        isOpen={!!transferUpdateStatusStore.error?.errorCode}
        onClose={() => $transferUpdateStatusStore.events.clear()}
        type='createTransfer'
        error={transferUpdateStatusStore.error}
        onCancel={
          transferUpdateStatusStore.error?.errorCode &&
          ([ErrorCode.TRP_VAL_008, ErrorCode.TRP_VAL_011] as ErrorCode[]).includes(transferUpdateStatusStore.error.errorCode)
            ? cancelHandler
            : undefined
        }
        onSubmit={() => confirmHandler(true)}
      />
    </>
  );
};
